import { Constants } from 'fhir-sdoh';
import moment from 'moment';
import type { ProgramBudget } from 'types';

export function weekDayCode(weekDay: string): number | undefined {
  switch (weekDay) {
    case 'SUNDAY':
      return 0;
    case 'MONDAY':
      return 1;
    case 'TUESDAY':
      return 2;
    case 'WEDNESDAY':
      return 3;
    case 'THURSDAY':
      return 4;
    case 'FRIDAY':
      return 5;
    case 'SATURDAY':
      return 6;
  }
}
/**
 * `dayINeed` matches its producer, `VendorOrderCutoff['cutOffDay']`
 * (`number | string | undefined`), rather than the `number` the arithmetic
 * assumes. `utils.spec` pins the `''` arm returning `undefined`.
 *
 * SUSPECTED DEFECT — the return type is `Moment | undefined` only because the
 * `''` guard exists. For `dayINeed === undefined` the guard PASSES (`undefined
 * !== ''`), both comparisons are false, and the final `else` calls
 * `moment(day).add(1, 'weeks').isoWeekday(undefined)` — which is moment's
 * GETTER overload and returns a NUMBER, not a Moment. `vendorUtils`'
 * `CheckOrderCutoff` then calls `cutOffDate!.format('MM/DD/YYYY')` on it and
 * throws "format is not a function".
 *
 * Both sides' assumptions: `CheckOrderCutoff` believes every `cutOffDay` it
 * passes is a weekday index; `VendorOrderCutoff` admits `undefined` precisely
 * because `weekDayCode` has no `default` arm. Unreachable today — `MF` and `AB`
 * seed `weekDayCode('TUESDAY')`/`('THURSDAY')`, which both match — so this fires
 * only if a vendor is given an unrecognised cutoff day name.
 *
 * Reported, not fixed: the declared return type would have to lie (or the guard
 * change) to describe the number case, and either hides the real problem, which
 * is that `weekDayCode` can miss.
 */
export function nextWeekDay(
  day: moment.MomentInput,
  dayINeed: number | string | undefined
): moment.Moment | undefined {
  if (dayINeed !== '') {
    const weekDay = moment(day).isoWeekday();

    // Asserted `number` at each arithmetic site rather than narrowed: a
    // `typeof`/`!= null` guard here would route the `undefined` and non-numeric
    // string cases somewhere they do not go today.
    if (weekDay === dayINeed) {
      return moment(day).isoWeekday(dayINeed);
    } else if (weekDay < (dayINeed as number)) {
      return moment(day)
        .add((dayINeed as number) - weekDay)
        .isoWeekday(dayINeed as number);
    } else {
      return moment(day).add(1, 'weeks').isoWeekday(dayINeed as number);
    }
  }
}

//(111) 222-3333
/**
 * `T` rather than `string`: a non-matching input is returned UNCHANGED, and
 * `utils.spec` pins `undefined` and `null` passing straight through.
 */
export function formatPhoneNumber<T>(phoneNumberString: T): T | string {
  try {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phoneNumberString;
  } catch {
    return phoneNumberString;
  }
}
//1112223333
export function formatPhoneNumberSimple<T>(phoneNumberString: T): T | string {
  try {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return match[1]! + match[2]! + match[3]!;
    }
    return phoneNumberString;
  } catch {
    return phoneNumberString;
  }
}
//make phone calls with amazon connect. here for now unless we need a separate file for connect functions
export const handleOutboundCall = (phoneNumber: string, orgId: string) => {
  //hardcoded list of org arns in dev
  const orgPhoneNumberMap = {
    'ASPI-SDOH-HC': Constants.PHONE_NUMBERS.ASPI,
    'IUH-CH': Constants.PHONE_NUMBERS.IUHCH,
    'IUH-BC': Constants.PHONE_NUMBERS['IUH-BC'],
  };
  // SUSPECTED DEFECT — `GET_PHONE_NUMBER_BY_ORG` returns `string | null` (it is
  // `CONNECT_QUEUE_ARN_MAP?.[orgId] ?? null`), so an org with no mapped queue
  // ARN reaches `connectOptions.queueARN` as `null`. The caller's declared
  // `string` says that cannot happen; the constant guarantees only the lookup.
  // Reported, not fixed: defaulting would place the outbound call on some other
  // org's queue.
  let qARN: string | null;
  if (orgId === 'ASPI-SDOH-HC' || orgId === 'IUH-CH' || orgId === 'IUH-BC') {
    qARN = orgPhoneNumberMap[orgId];
  } else {
    qARN = Constants.GET_PHONE_NUMBER_BY_ORG(orgId);
  }
  const endpoint = connect.Endpoint.byPhoneNumber(phoneNumber);
  //const qARN = orgPhoneNumberMap[orgId];
  const connectOptions: connect.ConnectOptions = {
    success: function () {},
    failure: function (err) {
      console.log('outbound call connection failed');
      console.log(err);
    },
    //need to specify for outbound phone calls
    queueARN: qARN as string,
  };
  const agent = new connect.Agent();
  agent.connect(endpoint, connectOptions);
};
// Return matching items
export function getCommonItems(arr1: string[], arr2: string[]): string[] {
  arr2 = arr2.map((item) => item.trim().toUpperCase());
  const set1 = new Set(arr2);
  const commonItems = arr1.filter((item) => set1.has(item.trim().toUpperCase()));
  return commonItems.map((item) => capitalizeFirstLetter(item.trim()));
}

/**
 * The entries of `arr2` that appear anywhere in `arr1` joined, compared
 * case-insensitively and returned capitalised.
 *
 * `arr2` may hold `undefined`: its callers build it by `flatMap`ping over
 * `FoodTaggingInfo`'s `non-allergens`, which declares only `Dairy`, so every other tag
 * yields `undefined`. Those entries throw on `.trim()` unless the caller filters them
 * first, which is why each call site does.
 */
// Checks for included string
export function getIncludeMatches(
  arr1: string[],
  arr2: (string | undefined)[],
  parser?: string
): string[] {
  const arr1String: string = arr1?.join(parser ? parser : ',').toUpperCase();

  const matched = arr2.filter((item) => {
    return arr1String.includes(item!.trim().toUpperCase());
  });
  return matched.map((item) => capitalizeFirstLetter(item!.trim()));
}
/**
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. Both `parser !== '' ? parser : ','` ternaries
 * below yield `undefined`, not `','`, when `parser` is omitted — the common call. The
 * `join(undefined)` still comma-joins, but `split(undefined)` returns the whole string
 * as a single element instead of splitting it, so a caller that omitted `parser` gets
 * back a one-element array rather than the cleaned list.
 *
 * `vendorUtils.tsx`'s `cleanedIngrediantList` is built exactly that way, so its
 * ingredient matching compares against one joined blob. `!== ''` was presumably meant
 * to be `!== undefined`; correcting it would change what the allergy tagging matches.
 */
// removes for included string
//false alrgen//ingredients
export function removeMatches(arr1: string[], arr2: string[], parser?: string) {
  let arr2String = arr2?.join(parser !== '' ? parser : ',').toUpperCase();
  arr1.forEach((item) => {
    arr2String = arr2String.replace(item.toUpperCase(), 'redacted allergen');
  });
  //replace
  return arr2String.split((parser !== '' ? parser : ',') as string);
}
export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/** Drops duplicates, and drops empty and absent entries along with them. */
export function getUniqueElements<T>(list: T[]): T[] {
  return list.filter((c, index) => {
    if (c && (c as unknown) !== '') return list.indexOf(c) === index;
    return false;
  });
}

/**
 * Adds `programOrderCost` to the existing cost on the matching referral's budget.
 *
 * `parseFloat` is declared to take a `string` but both arguments here are
 * `number | undefined` — `ProgramBudget['programOrderCost']` is a `number` and
 * `utils.spec` passes numeric literals. `parseFloat` stringifies its argument
 * first, so a number round-trips unchanged; the assertions record that the two
 * sides disagree about the type, not that a conversion is happening.
 */
export function updateProgramBudgetByReferralId(
  referralId: string | undefined,
  programOrderCost: number | undefined,
  programsBudget: ProgramBudget[],
  setProgramBudget: (budgets: ProgramBudget[]) => void
): ProgramBudget[] {
  if (!referralId) {
    console.error('Referral ID is required to set the budget.');
    return [];
  }

  if (programsBudget.length <= 0) {
    console.error('programsBudget is empty, cannot update budget.');
    return [];
  }

  const tempBudget = programsBudget.map((budget) => {
    if (budget.referralId === referralId) {
      let tempProgramOrderCost =
        parseFloat((budget.programOrderCost || 0) as unknown as string) +
        parseFloat((programOrderCost || 0) as unknown as string);
      tempProgramOrderCost = Math.round(tempProgramOrderCost * 100) / 100;
      budget.programOrderCost = tempProgramOrderCost;
    }
    return budget;
  });
  //localStorage.setItem('programBudget', tempBudget || []);
  setProgramBudget(tempBudget || []);

  return tempBudget;
}

export function setProgramBudgetByReferralId(
  referralId: string | undefined,
  programOrderCost: number | undefined,
  programsBudget: ProgramBudget[],
  setProgramBudget: (budgets: ProgramBudget[]) => void
): ProgramBudget[] {
  if (!referralId) {
    console.error('Referral ID is required to set the budget.');
    return [];
  }

  if (programsBudget.length <= 0) {
    console.error('programsBudget is not empty, returning existing budget.');
    return [];
  }

  const tempBudget = programsBudget.map((budget) => {
    if (budget.referralId === referralId) {
      // Writes `undefined` into an optional key rather than deleting it, which
      // `exactOptionalPropertyTypes` distinguishes. Guarding would leave the
      // previous cost in place instead of clearing it.
      budget.programOrderCost = programOrderCost as number;
    }
    return budget;
  });
  setProgramBudget(tempBudget || []);
  return tempBudget;
}

export function getProgramBudget(
  referralId: string | undefined,
  programsBudget: ProgramBudget[]
): ProgramBudget {
  if (!referralId) {
    console.error('Referral ID is required to get the budget.');
    return {};
  }
  const budget = programsBudget.find((budget) => budget.referralId === referralId);
  return budget || {};
}

export function getReferralProgramOrderCost(
  referralId: string | undefined,
  programsBudget: ProgramBudget[] = []
): number | undefined {
  const budget = programsBudget.find((budget) => budget.referralId === referralId);
  return budget ? budget.programOrderCost : 0;
}

export function getReferralProgramBudget(
  referralId: string | undefined,
  programsBudget: ProgramBudget[] = []
): number | undefined {
  const budget = programsBudget.find((budget) => budget.referralId === referralId);
  return budget ? budget.programOrderCost : 0;
}

export function resetProgramOrderCost(
  programsBudget: ProgramBudget[] | undefined,
  setProgramBudget?: (budgets: ProgramBudget[]) => void
): ProgramBudget[] | undefined {
  if (programsBudget && programsBudget.length <= 0) {
    console.error('programsBudget is not empty, returning existing budget.');
    return [];
  }

  const tempBudget = programsBudget?.map((budget) => {
    budget.programOrderCost = 0;

    return budget;
  });

  setProgramBudget?.(tempBudget || []);
  return tempBudget;
}
/** For JSON.stringify's second argument, to handle any circular references. */
export const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key: string, value: unknown) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
    }
    return value;
  };
};
export const printPrettyJSON = (rawJSON: unknown) => {
  return JSON.stringify(rawJSON, getCircularReplacer(), 4);
};
